import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock } from 'lucide-react';
import Header from './Navbar/Navbar';
import Sidebar from './Sidebar/Sidebar';
import MobileNav from './Navbar/MobileNav';
import { agentService } from '../services/agent/agentService';
import { useAgent } from '../context/agent/AgentContext';
import FloatingVoucherLoader from '../components/feedback/FloatingVoucherLoader';
import GlobalSearchResults from '../components/layout/GlobalSearchResults';
import { initAnalytics, trackPageView } from '../utils/analytics';

import { useSubscription } from '../context/subscription/SubscriptionContext';

const MainLayout = ({ children, activeTab, setActiveTab }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userData, setUserData, setAgent } = useAgent();
    const { refreshSubscription } = useSubscription();

    const [searchTerm, setSearchTerm] = useState('');

    const subUserRole = localStorage.getItem('sub_user_role');
    const isBookingsRole = subUserRole === 'bookings';
    const isOperationsRole = subUserRole === 'operations';

    const restrictedPathsForBookings = [
        '/partners',
        '/post-connect',
        '/packages',
        '/notifications',
        '/enquiries',
        '/role-management',
        '/tracking',
        '/wallet'
    ];

    const restrictedPathsForOperations = [
        '/voucher',
        '/create-voucher',
        '/voucher-edit',
        '/voucher-dashboard',
        '/voucher-itinerary',
        '/role-management',
        '/wallet'
    ];

    const isRestrictedForBookings = isBookingsRole && restrictedPathsForBookings.some(path => location.pathname.startsWith(path));
    const isRestrictedForOperations = isOperationsRole && restrictedPathsForOperations.some(path => location.pathname.startsWith(path));
    const isRestrictedRoute = isRestrictedForBookings || isRestrictedForOperations;
    const restrictedRoleName = isRestrictedForBookings ? 'Bookings' : 'Operations';

    useEffect(() => {
        initAnalytics();
        trackPageView(location.pathname + location.search);
    }, [location]);

    useEffect(() => {
        if (
            localStorage.getItem('agent_id') === null ||
            localStorage.getItem('token_agents') === null
        ) {
            navigate('/login');
            return;
        }

        const fetchUserData = async () => {
            try {
                const response = await agentService.getProfile();
                const data = response;
                setUserData(data.data);
                setAgent(data.data?.agent);
            } catch (error) {
                console.error('Error fetching user data:', error);
                if (error.response && error.response.status === 400) {
                    localStorage.removeItem('token_agents');
                    localStorage.removeItem('agent_id');
                    sessionStorage.clear();
                    navigate('/login');
                }
            }
        };

        fetchUserData();
        refreshSubscription();
    }, [navigate, setAgent, setUserData, refreshSubscription]);

    return (
        <div className="h-screen bg-slate-50 flex flex-col font-sans text-gray-800 overflow-hidden">
            <Header
                userData={userData}
                onOpenProfile={() => navigate('/profile')}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
            />

            <div className="flex flex-1 overflow-hidden">
                {/* Sidebar Navigation */}
                <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
                {!(location.pathname === '/support' && (new URLSearchParams(location.search).get('id') || new URLSearchParams(location.search).get('action') === 'new')) && (
                    <MobileNav activeTab={activeTab} setActiveTab={setActiveTab} />
                )}

                {/* Main Content Area - ONLY THIS AREA SCROLLS */}
                <main className="flex-1 h-full overflow-y-auto w-full relative z-10 overflow-x-hidden custom-scrollbar">
                    <div className="w-full pb-4 sm:pb-6">
                        {searchTerm && searchTerm.length > 2 ? (
                            <GlobalSearchResults searchTerm={searchTerm} onClose={() => setSearchTerm('')} />
                        ) : (
                            <>
                                {isRestrictedRoute ? (
                                    <div className="min-h-[400px] flex flex-col items-center justify-center bg-white rounded-3xl p-8 border border-gray-100 shadow-sm text-center my-6">
                                        <div className="w-16 h-16 bg-rose-50 text-rose-500 rounded-2xl flex items-center justify-center mb-4">
                                            <Lock size={32} />
                                        </div>
                                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Restricted</h2>
                                        <p className="text-gray-500 max-w-md text-sm">
                                            Your role (<span className="font-bold text-gray-800">{restrictedRoleName}</span>) does not have permission to access this page.
                                        </p>
                                        <button
                                            onClick={() => navigate('/dashboard')}
                                            className="mt-6 px-6 py-2.5 bg-[#067978] hover:bg-[#05605f] text-white font-bold text-sm rounded-xl transition-all shadow-lg shadow-teal-900/20"
                                        >
                                            Go to Dashboard
                                        </button>
                                    </div>
                                ) : (
                                    children
                                )}
                            </>
                        )}
                    </div>
                </main>
            </div>

            <FloatingVoucherLoader />
        </div>
    );
};

export default MainLayout;
